import { useContext, useEffect, useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { AuthContext } from '../../context/auth-context'
import { authErrorTranslate } from '../../firebase/auth/auth-error-translate'
import { kLinkPrivacyPolicyUrl, kLinkTermsOfServiceUrl } from '@/constants/constants-links'
import { PaxtonDisclaimer } from '@/components/disclaimers/PaxtonDisclaimer'
import googleAuthRedirect from '../../firebase/auth/google-auth-redirect'
import { Button } from '@/components/button/Button'
import { Link } from '@/components/link/Link'
import ReCAPTCHA from 'react-google-recaptcha'
import { EmailAuthProvider, getAuth, validatePassword } from 'firebase/auth'
import InlineTipError from '../inline-tips/InlineTipError'
import activateReferral from '@/referral/fetch/post-activate-referral'
import { useAppDispatch } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationShortMs, kToastDurationMediumMs } from '@/constants/constants-ui'
import * as Sentry from '@sentry/browser'
import { removeReferralCode, retrieveReferralCode, validateReferralCodeAndUserAccount } from '@/referral/referral-utils'
import ValidationError from '@/util/errors/validation-error'
import ReferralInviteMessage from '@/referral/views/referral-invite-message'
import { brandedAIFriendlyName } from '@/util/enterprise'
import { CheckCircleUnchecked, CheckCircleFilled } from '@/assets/icons'
import clsx from 'clsx'
import FloatLabelFormInput from '@/matter/components/matter-dialogs/FloatLabelFormInput'

type FormInputs = {
  email: string
  password: string
}

type SignUpFormProps = {
  switchToSignIn: () => void
  onSuccess: () => void
  tooltipMessage?: string
  title?: string
  allowReferral?: boolean
  defaultEmail?: string
}

export default function SignUpForm(props: SignUpFormProps) {
  const { switchToSignIn, onSuccess, tooltipMessage, title, allowReferral, defaultEmail } = props

  const dispatch = useAppDispatch()
  const auth = getAuth()
  const authContext = useContext(AuthContext)
  const user = authContext?.user
  const uiTimeout = 750
  const timeoutIdsRef = useRef<{ [key: string]: NodeJS.Timeout | null }>({})
  const initialCriteriaState = { length: false, upperAndLowerCase: false, number: false, special: false }

  // Redirect if the user is not null
  useEffect(() => {
    if (user != null && !user.isAnonymous) {
      onSuccess()
    }
  }, [user])

  // Local State
  const [validationError, setValidationError] = useState('')
  const [loading, setLoading] = useState(false)
  const [recaptcha, setRecaptcha] = useState(null)
  const [referralCodeError, setReferralCodeError] = useState('')
  const [referralCode, setReferralCode] = useState<string | null>(null)
  const [passwordCriteria, setPasswordCriteria] = useState(initialCriteriaState)
  const [criteriaUIVisibility, setCriteriaUIVisibility] = useState({
    length: true,
    upperAndLowerCase: true,
    number: true,
    special: true,
  })
  const isPasswordValid = Object.values(passwordCriteria).every(Boolean)
  const [uiPasswordSectionVisible, setUiPasswordSectionVisible] = useState(false)
  const passwordSectionHideTimerRef = useRef<NodeJS.Timeout | null>(null)
  const prevIsPasswordValidRef = useRef<boolean>(isPasswordValid)

  // Initialize referral code state
  useEffect(() => {
    if (allowReferral) {
      const { code, error } = retrieveReferralCode()

      if (code) {
        setReferralCode(code)
      }
      if (error) {
        setReferralCodeError(error)
      }
    }
  }, [allowReferral])

  // Handle recaptcha change
  function onRecaptchaChange(value: any) {
    setRecaptcha(value)
  }

  // Form Hook
  const { register, handleSubmit, watch } = useForm<FormInputs>({
    defaultValues: {
      email: defaultEmail || '',
      password: '',
    },
  })

  const watchedPassword = watch('password')

  // Effect to validate password in real-time (for individual criteria)
  useEffect(() => {
    const checkPasswordValidity = async () => {
      if (watchedPassword && watchedPassword.length > 0) {
        try {
          const status = await validatePassword(auth, watchedPassword)
          const newCriteria = {
            length: status.meetsMinPasswordLength ?? false,
            upperAndLowerCase: (status.containsUppercaseLetter ?? false) && (status.containsLowercaseLetter ?? false),
            number: status.containsNumericCharacter ?? false,
            special: status.containsNonAlphanumericCharacter ?? false,
          }

          const newCriteriaKeys = Object.keys(newCriteria) as Array<keyof typeof newCriteria>
          newCriteriaKeys.forEach((key) => {
            const isNowMet = newCriteria[key]
            const wasPreviouslyMet = passwordCriteria[key]

            if (isNowMet && !wasPreviouslyMet) {
              // Criterion just met
              const timeoutId = timeoutIdsRef.current[key]
              if (timeoutId) clearTimeout(timeoutId)
              timeoutIdsRef.current[key] = setTimeout(() => {
                setCriteriaUIVisibility((prev) => ({ ...prev, [key]: false }))
              }, uiTimeout)
            } else if (!isNowMet && wasPreviouslyMet) {
              // Criterion no longer met
              const timeoutId = timeoutIdsRef.current[key]
              if (timeoutId) clearTimeout(timeoutId)
              timeoutIdsRef.current[key] = null
              setCriteriaUIVisibility((prev) => ({ ...prev, [key]: true }))
            }
          })
          setPasswordCriteria(newCriteria)
        } catch (error) {
          console.warn('Error validating password:', error)
          // Reset criteria on error to the initial state
          setPasswordCriteria(initialCriteriaState)
          setCriteriaUIVisibility({ length: true, upperAndLowerCase: true, number: true, special: true })
          Object.keys(timeoutIdsRef.current).forEach((key) => {
            const timeoutId = timeoutIdsRef.current[key]
            if (timeoutId) clearTimeout(timeoutId)
            timeoutIdsRef.current[key] = null
          })
        }
      } else {
        // Reset criteria if password is empty to the initial state
        setPasswordCriteria(initialCriteriaState)
        setCriteriaUIVisibility({ length: true, upperAndLowerCase: true, number: true, special: true })
        Object.keys(timeoutIdsRef.current).forEach((key) => {
          const timeoutId = timeoutIdsRef.current[key]
          if (timeoutId) clearTimeout(timeoutId)
          timeoutIdsRef.current[key] = null
        })
      }
    }
    checkPasswordValidity()
  }, [watchedPassword, auth]) // Removed passwordCriteria from deps, it's set here.

  // Effect to control visibility of the entire password requirements section
  useEffect(() => {
    const naturallyShowSection = watchedPassword && watchedPassword.length > 0 && !isPasswordValid

    if (passwordSectionHideTimerRef.current) {
      clearTimeout(passwordSectionHideTimerRef.current)
      passwordSectionHideTimerRef.current = null
    }

    if (naturallyShowSection) {
      setUiPasswordSectionVisible(true)
    } else {
      // Section should naturally be hidden (password empty or password valid)
      if (watchedPassword && watchedPassword.length > 0 && isPasswordValid && prevIsPasswordValidRef.current === false) {
        // Password just became valid. Keep section visible for 1 more second.
        // uiPasswordSectionVisible is likely already true from the naturallyShowSection block in a previous render.
        passwordSectionHideTimerRef.current = setTimeout(() => {
          setUiPasswordSectionVisible(false)
        }, uiTimeout)
      } else {
        // Password is empty, or was already valid and remains valid. Hide immediately.
        setUiPasswordSectionVisible(false)
      }
    }
    prevIsPasswordValidRef.current = isPasswordValid

    return () => {
      if (passwordSectionHideTimerRef.current) {
        clearTimeout(passwordSectionHideTimerRef.current)
      }
    }
  }, [watchedPassword, isPasswordValid])

  // Cleanup timeouts on unmount
  useEffect(() => {
    const currentTimeoutIds = timeoutIdsRef.current
    return () => {
      Object.values(currentTimeoutIds).forEach((timeoutId) => {
        if (timeoutId) clearTimeout(timeoutId)
      })
    }
  }, [])

  // Sign in error component
  function ValidationErrorMessage() {
    if (validationError != '') {
      return (
        <div>
          <p className="text-center text-red-700">{validationError}</p>
        </div>
      )
    } else {
      return null
    }
  }

  async function handleReferralActivation(referralCode: string) {
    const userAccountData = authContext?.userAccountData

    try {
      validateReferralCodeAndUserAccount(referralCode, userAccountData)

      await activateReferral(referralCode)

      const successMessage = 'Referral activated!'
      dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: successMessage, durationMs: kToastDurationShortMs }))
    } catch (e: any) {
      console.error('Referral activation failed: ', e.message)

      const errorMessage = `Account created, but the referral could not be activated. ${brandedAIFriendlyName} has been notified.`
      dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: errorMessage, durationMs: kToastDurationMediumMs }))

      if (e instanceof ValidationError) {
        // Capture validation errors in Sentry, other errors are already captured in activateReferral call
        Sentry.captureException(e, {
          extra: {
            referralCode,
            subscriptionStatus: userAccountData?.subscriptionStatus,
            isOrgAccount: userAccountData?.isOrgAccount,
            brand: userAccountData?.brand,
          },
        })
      }
    } finally {
      removeReferralCode()
    }
  }

  // Handle Sign Up
  async function handleSignUp(data: FormInputs) {
    setValidationError('')
    setLoading(true)

    if (!isPasswordValid) {
      setValidationError('The password does not meet all requirements.')
      setLoading(false)
      // Focus on the password input field, ref doesn't work
      const passwordInput = document.getElementById('password') as HTMLInputElement
      if (passwordInput) {
        passwordInput.focus()
      }
      return
    }

    if (recaptcha == null) {
      setValidationError('Please complete the recaptcha')
      setLoading(false)
      return
    }

    try {
      console.log('Creating account for anonymous user: ', user)

      // Create the email / pass credential for this user
      const credential = EmailAuthProvider.credential(data.email, data.password)

      // Link the credential and update the AuthContext
      await authContext?.linkCredential(credential)

      // Only trigger referral activation if referrals are allowed and referral code is present
      if (allowReferral && referralCode) {
        await handleReferralActivation(referralCode)
      }

      // onSuccess
      onSuccess()
    } catch (e: any) {
      const validationError = await authErrorTranslate(e, data.email)
      setValidationError(validationError)

      console.warn(e)
      Sentry.captureException(e, {
        extra: {
          email: data.email,
        },
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {tooltipMessage && <div className={'mb-4'}>{InlineTipError(tooltipMessage)}</div>}
      <h2 className="mb-4 text-center text-2xl font-bold tracking-tight text-brand-neutral-900">{title ?? 'Start your free trial'}</h2>
      <form className="space-y-4" method="POST" onSubmit={handleSubmit((data) => handleSignUp(data))}>
        <div className="mt-2">
          <FloatLabelFormInput
            label="Work email address"
            id="email-address"
            required
            registration={register('email', { required: true })}
            disabled={!!defaultEmail}
            inlineMessage={defaultEmail ? 'This invite can only be accepted by the invited email address.' : undefined}
            inputProps={{
              type: 'email',
              autoComplete: 'email',
            }}
            tooltipProps={{
              title: defaultEmail ?? '',
              copyToClipboard: true,
            }}
          />
        </div>

        <div>
          <div className="mt-2">
            <FloatLabelFormInput
              label="Create a password"
              id="password"
              required
              registration={register('password', { required: true })}
              inputProps={{
                type: 'password',
                autoComplete: 'current-password',
              }}
            />
          </div>
          {uiPasswordSectionVisible && (
            <div className="text-sm text-brand-neutral-600 border border-brand-neutral-300 rounded-md p-2 mt-2">
              <div className="text-neutral-950">Password requirements:</div>
              {criteriaUIVisibility.length && (
                <div className="flex items-center">
                  {passwordCriteria.length ? <CheckCircleFilled className="text-green-800" /> : <CheckCircleUnchecked />}
                  <span
                    className={clsx('ml-1', {
                      'text-green-800': passwordCriteria.length,
                    })}
                  >
                    At least 8 characters
                  </span>
                </div>
              )}
              {criteriaUIVisibility.upperAndLowerCase && (
                <div className="flex items-center">
                  {passwordCriteria.upperAndLowerCase ? <CheckCircleFilled className="text-green-800" /> : <CheckCircleUnchecked />}
                  <span
                    className={clsx('ml-1', {
                      'text-green-800': passwordCriteria.upperAndLowerCase,
                    })}
                  >
                    Both uppercase and lowercase letters
                  </span>
                </div>
              )}
              {criteriaUIVisibility.number && (
                <div className="flex items-center">
                  {passwordCriteria.number ? <CheckCircleFilled className="text-green-800" /> : <CheckCircleUnchecked />}
                  <span
                    className={clsx('ml-1', {
                      'text-green-800': passwordCriteria.number,
                    })}
                  >
                    At least one number
                  </span>
                </div>
              )}
              {criteriaUIVisibility.special && (
                <div className="flex items-center">
                  {passwordCriteria.special ? <CheckCircleFilled className="text-green-800" /> : <CheckCircleUnchecked />}
                  <span
                    className={clsx('ml-1', {
                      'text-green-800': passwordCriteria.special,
                    })}
                  >
                    At least one special character
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={'g-recaptcha'} data-sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}></div>

        <ReCAPTCHA sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY} onChange={onRecaptchaChange} />

        {allowReferral && referralCode && (
          <div className={'mb-4'}>
            <ReferralInviteMessage />
          </div>
        )}
        {allowReferral && referralCodeError && <div className={'mb-4'}>{InlineTipError(referralCodeError)}</div>}

        <div>
          <Button type="submit" fullWidth loading={loading}>
            Sign up
          </Button>
        </div>

        <ValidationErrorMessage />
      </form>

      <p className="mt-5 text-center text-sm text-brand-neutral-500">
        Already have an account?{' '}
        <Button variant="plain" size="sm" onClick={switchToSignIn}>
          Sign in
        </Button>
      </p>

      <div>
        <div className="relative mt-5">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-sm font-medium leading-6">
            <span className="bg-brand-neutral-50 px-6 text-brand-neutral-900">Or continue with</span>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4">
          <button
            onClick={() => googleAuthRedirect()}
            className="flex w-full items-center justify-center gap-3 rounded-md bg-[#ffffff] px-3 py-1.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#24292F] border hover:shadow transition duration-150"
          >
            <img className="w-6 h-6" src="https://www.svgrepo.com/show/475656/google-color.svg" loading="lazy" alt="google logo" />
            <span className="text-sm font-semibold leading-6">Google</span>
          </button>
        </div>

        <div className="mt-5 text-xs text-center">
          By creating an account, you agree to our{' '}
          <Link href={kLinkTermsOfServiceUrl} external size="xs" className="whitespace-nowrap">
            terms of service
          </Link>
          &nbsp;and&nbsp;
          <Link href={kLinkPrivacyPolicyUrl} external size="xs" className="whitespace-nowrap">
            privacy policy
          </Link>
          .
        </div>
        <div className="mt-5 text-xs text-center">
          <PaxtonDisclaimer />
        </div>
      </div>
    </>
  )
}
